import styled, { css } from 'styled-components';

export const PlanCardTopSection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 32px 32px 0 32px;
    border-radius: 16px 16px 0 0;
    background: ${theme.v3.colors.neutral[100]};
  `}
`;

export const PlanCardBottomSection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 32px;
    border-radius: 0 0 16px 16px;
    background: ${theme.v3.colors.neutral[100]};
  `}
`;

const getPlanCardBackground = (theme, isCurrent, isRecommended) => {
  if (isCurrent) {
    return theme.v3.colors.neutral[95];
  }
  if (isRecommended) {
    return `linear-gradient(90deg,
      ${theme.v3.colors.brand.snappyBlue[60]} 0%,
      #02AEF2 20%,
      #2BC5EF 40%,
      #5CDAE9 60%,
      #8BEDE6 80%,
      ${theme.v3.colors.accent.green[95]} 100%
    )`;
  }
  return theme.v3.colors.neutral[100];
};

export const PlanCardWrapper = styled.div`
  ${({ theme, isCurrent, isRecommended }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 ${isCurrent ? '300px' : '320px'};
    height: ${isCurrent ? '458px' : 'auto'};
    border-radius: calc(${theme.v3.border.radius.xl} + 2px);
    background: ${getPlanCardBackground(theme, isCurrent, isRecommended)};

    @media (max-width: ${theme.v3.breakpoints.md}) {
      flex: 1 1 auto;
      width: 100%;
      height: auto;
    }
  `}
`;

export const PlanCardLabel = styled.p`
  ${({ theme, isCurrent, isRecommended }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    text-align: center;
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    background: ${getPlanCardBackground(theme, isCurrent, isRecommended)};
    color: ${isCurrent ? theme.v3.colors.neutral[30] : theme.v3.colors.brand.snappyBlue[10]};
    margin: 0;
    padding: 10px 0;
    white-space: nowrap;
    z-index: 1;
    border-radius: 16px 16px 0 0;
  `}
`;

export const PlanCardInner = styled.div`
  ${({ theme, hasLabel }) => css`
    padding: 0;
    gap: 0;
    display: flex;
    flex-direction: column;
    margin: ${hasLabel ? '0' : '43px 0 0'};
    top: ${hasLabel ? '43px' : '0'};
    width: calc(100% - 4px);
    flex: 1;
    box-sizing: border-box;
    position: relative;
    z-index: 2;
    border-radius: ${!hasLabel ? '16px' : '16px 16px 0 0'};
    border: ${!hasLabel ? `1px solid ${theme.v3.colors.neutral[90]}` : 'none'};
  `}
`;

export const PlanCardHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

export const PlanCardName = styled.h4`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.large};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    color: ${theme.v3.colors.neutral[20]};
    margin: 0;
    white-space: nowrap;
  `}
`;

export const PlanCardDiscountBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 30px;
    background-color: ${theme.v3.colors.contextual.active[98]};
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    color: ${theme.v3.colors.contextual.active[30]};
    white-space: nowrap;
  `}
`;

export const PlanCardPrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

export const PlanCardMonthlyPriceWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    gap: 2px;
    color: ${theme.v3.colors.brand.snappyBlue[20]};
    white-space: nowrap;
  `}
`;

export const PlanCardCurrencySymbol = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.regular};
    line-height: ${theme.v3.font.lineHeight.comfortable};
  `}
`;

export const PlanCardMonthlyPrice = styled.span`
  ${({ theme }) => css`
    font-size: 32px;
    font-weight: ${theme.v3.font.weights.medium};
    line-height: 1;
  `}
`;

export const PlanCardMonthlyPriceSuffix = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.regular};
    line-height: ${theme.v3.font.lineHeight.comfortable};
  `}
`;

export const PlanCardFullPrice = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    color: ${theme.v3.colors.neutral[50]};
    text-decoration: line-through;
    margin-top: 2px;
  `}
`;

export const PlanCardCyclePrice = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    color: ${theme.v3.colors.neutral[50]};
    min-height: 24px;
  `}
`;

export const PlanCardResourcesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PlanCardResourcesItem = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    color: ${theme.v3.colors.neutral[20]};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  `}
`;

export const PlanCardResourcesItemValue = styled.strong`
  ${({ theme }) => css`
    font-weight: ${theme.v3.font.weights.medium};
  `}
`;

export const PlanCardResourcesItemSuffix = styled.span`
  ${({ theme }) => css`
    font-weight: ${theme.v3.font.weights.regular};
  `}
`;

export const PlanCardResourceDeltaBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    height: 24px;
    border-radius: 30px;
    background-color: ${theme.v3.colors.brand.snappyBlue[95]};
    font-size: ${theme.v3.font.sizes.small};
    font-weight: ${theme.v3.font.weights.medium};
    color: ${theme.v3.colors.neutral[20]};
    white-space: nowrap;
  `}
`;

export const CarouselNavSpacer = styled.div`
  height: 50px;
  flex-shrink: 0;
`;

export const PlanCardPricesPlaceholder = styled.div`
  visibility: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: calc(32px + 4px + 21px + 4px + 18px);
`;

export const PlanCardButtonPlaceholder = styled.div`
  visibility: hidden;
  width: 100%;
  min-height: 56px;
`;

export const PlanCardButtonWrapper = styled.div`
  ${({ theme, isRecommended }) => css`
    width: 100%;
    margin-top: auto;

    button {
      background: ${isRecommended
    ? `linear-gradient(90deg,
          ${theme.v3.colors.brand.snappyBlue[60]} 0%,
          #02AEF2 20%,
          #2BC5EF 40%,
          #5CDAE9 60%,
          #8BEDE6 80%,
          ${theme.v3.colors.accent.green[95]} 100%
        )`
    : theme.v3.colors.brand.snappyBlue[50]};
      color: ${isRecommended ? theme.v3.colors.brand.snappyBlue[10] : theme.v3.colors.neutral[100]};
      border: none !important;
      border-radius: 30px;

      &:disabled {
        background: ${theme.v3.colors.neutral[90]};
        color: ${theme.v3.colors.neutral[70]};
        border: none !important;
        opacity: 1 !important;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        color: ${isRecommended ? theme.v3.colors.brand.snappyBlue[10] : theme.v3.colors.neutral[100]};
        opacity: ${isRecommended ? 0.7 : 1};
        border: none;
    }
  `}
`;
